import { SphereDropdownMenuItem } from "@components/common/dropdown/sphere-dropdown-selector-item";
import { SphereDropdownSelectorProps } from "@components/common/dropdown/sphere-dropdown-selector-types";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { ArrowDownIcon, Text, FaroTextVariant, neutral, blue } from "@flat-ui";
import {
  DEBOUNCE_DELAY_MS,
  useDebouncedCallback,
} from "@hooks/use-debounce-callback";
import { ButtonBase, Fade, Menu, Stack } from "@mui/material";
import { MouseEvent, useMemo, useState } from "react";

/** MAX menu height and create scroll after that */
export const MENU_MAX_HEIGHT = 250;

/** Renders a dropdown with selectable items */
export function SphereDropdownSelector<T extends string | number>({
  items,
  currentValue,
  handleChange,
  defaultLabel,
  iconColor = neutral[1000],
  variant = FaroTextVariant.bodyM,
  sxItem,
  sxLabel,
  sxDescription,
  sxMenu,
  menuPosition,
  menuMaxHeight = MENU_MAX_HEIGHT,
  menuWidth = "7.875rem",
  transition = Fade,
  buttonTooltipText,
}: SphereDropdownSelectorProps<T>): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const debouncedHandleChange = useDebouncedCallback(
    handleChange,
    DEBOUNCE_DELAY_MS
  );

  const currentValueLabel = useMemo(() => {
    const currentValueItem = items.find((item) => item.value === currentValue);
    return currentValueItem?.label;
  }, [currentValue, items]);

  function onValueChange(value: T): void {
    debouncedHandleChange(value);
    setAnchorEl(null);
  }

  function handleOpen(event: MouseEvent<HTMLElement>): void {
    setAnchorEl(event.currentTarget);
  }

  return (
    <Stack height="100%" justifyContent="center">
      <SphereTooltip title={buttonTooltipText}>
        <ButtonBase
          sx={{
            gap: "0.375rem",
          }}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={handleOpen}
        >
          <Text
            variant={variant}
            sx={{
              ...sxLabel,
            }}
          >
            {currentValueLabel || defaultLabel}
          </Text>
          <ArrowDownIcon
            data-testid="dropdown-selector-arrow"
            sx={{
              width: "1rem",
              height: "1rem",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
              color: iconColor,
            }}
          />
        </ButtonBase>
      </SphereTooltip>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => {
          debouncedHandleChange.cancel();
          setAnchorEl(null);
        }}
        anchorOrigin={
          menuPosition?.anchorOrigin ?? {
            vertical: "bottom",
            horizontal: "right",
          }
        }
        transformOrigin={
          menuPosition?.transformOrigin ?? {
            vertical: "top",
            horizontal: "right",
          }
        }
        slotProps={{
          paper: {
            sx: {
              minWidth: menuWidth,
              maxHeight: menuMaxHeight,
              overflowY: "auto",
            },
          },
        }}
        sx={{
          "& .MuiMenuItem-root": {
            marginX: "0.5rem",
            "&:hover": {
              color: blue[500],
            },
            minHeight: "fit-content",
          },
          ...sxMenu,
        }}
        slots={{ transition }}
      >
        {items.map((item) => (
          <SphereDropdownMenuItem
            key={item.value}
            item={item}
            currentValue={currentValue}
            handleChange={onValueChange}
            sxItem={sxItem}
            sxDescription={sxDescription}
          />
        ))}
      </Menu>
    </Stack>
  );
}
